<script setup lang="ts">
import { dayjs } from '@revolugo/common/utils'
import Divider from 'primevue/divider'
import { useI18n } from 'vue-i18n'

import { RDetailsList, RDetailsListItem } from '#atoms'
import { useMoney } from '#composables'

import type { RPriceSummaryProps, SummaryItem } from './RPriceSummary.types.ts'

const { locale, t } = useI18n()

const props = withDefaults(defineProps<RPriceSummaryProps>(), {
  variant: 'day',
})

const { formattedValue: totalTaxesFormattedValue } = useMoney(
  {
    currency: props.currency,
    value: props.taxAmount || 0,
  },
  locale,
)

const { formattedValue: subTotalFormattedValue } = useMoney(
  {
    currency: props.currency,
    value: props.subTotal || 0,
  },
  locale,
)

const { formattedValue: totalFormattedValue } = useMoney(
  {
    currency: props.currency,
    value: props.total,
  },
  locale,
)

const itemUseMoney = (item: SummaryItem) => {
  const useItemMoney = useMoney(
    {
      currency: item.currency,
      value: item.price,
    },
    locale,
  )

  return useItemMoney
}
</script>

<template>
  <div :class="`${variant}`">
    <h6
      v-if="title"
      class="h6-semibold mb-4"
    >
      {{ title }}
    </h6>
    <RDetailsList
      class="mb-4"
      data-testid="list-item"
    >
      <RDetailsListItem
        v-for="item of list"
        class="!border-b border-surface-200 pb-4"
      >
        <template #title>
          <h6 class="text-gray-950 body-1-semibold">{{ item.title }}</h6>
        </template>
        <template
          v-if="item?.descriptions?.length"
          #subtitle
        >
          <span
            v-for="subItem in item.descriptions"
            class="body-2-medium"
          >
            {{ subItem.count }} x {{ subItem.description }}
          </span>
          <span>
            {{
              t('night', {
                count: dayjs(item.checkOutDate).diff(item.checkInDate, 'day'),
              })
            }}&nbsp;: {{ dayjs(item.checkInDate).locale(locale).format('LL') }}
            {{ t('to') }}
            {{ dayjs(item.checkOutDate).locale(locale).format('LL') }}
          </span>
        </template>
        <template
          v-if="itemUseMoney(item)?.formattedValue"
          #extra
        >
          <span class="text-gray-500">{{
            itemUseMoney(item)?.formattedValue
          }}</span>
        </template>
      </RDetailsListItem>
    </RDetailsList>

    <RDetailsList>
      <RDetailsListItem v-if="subTotal">
        <template #title>
          <h6 class="body-1-semibold text-gray-950">{{ t('subTotal') }}</h6>
        </template>
        <template #extra>
          <h6 class="h6-semibold">{{ subTotalFormattedValue }}</h6>
        </template>
      </RDetailsListItem>
      <RDetailsListItem>
        <template #title>
          <h6 class="text-gray-950 body-1-semibold">
            {{ t('taxes') }}
          </h6></template
        >
        <template #extra>
          <h6 class="text-gray-500">
            {{ totalTaxesFormattedValue }}
          </h6></template
        >
      </RDetailsListItem>
    </RDetailsList>

    <Divider
      type="solid"
      class="flex bg-neutral-200 h-px my-4"
    />

    <RDetailsList>
      <RDetailsListItem>
        <template #title>
          <h6 class="body-1-semibold text-gray-950">{{ t('total') }}</h6>
        </template>
        <template #extra>
          <h6 class="h6-semibold">{{ totalFormattedValue }}</h6>
        </template>
      </RDetailsListItem>
    </RDetailsList>
  </div>
</template>

<style lang="scss" scoped>
.night {
  color: var(--p-neutral-0);
  background-color: var(--p-neutral-950);

  :deep dl > div {
    border-color: var(--p-neutral-800);
  }

  :deep h6 {
    color: var(--p-neutral-0);
  }

  :deep dt > span {
    color: var(--p-neutral-300);
  }

  :deep dd > span {
    color: var(--p-neutral-0) !important;
  }
}
</style>

<i18n lang="json" src="./RPriceSummary.i18n.json"></i18n>
